import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  Popover,
  Radio,
  Select,
  message,
  DatePicker,
  Button
} from 'antd'
import type { RadioChangeEvent } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import Advance from './Advance'
import sensors, { sensorsRole } from '../../../../utils/sensors'
import naming_icon from '../../../../images/naming_icon.png'
import contact_us_icon from '../../../../images/contact_us.png'
import { useAppSelector } from '../../../../app/hooks'

import {
  sliceCharactersLen,
  raplaceEmoji,
  filterIllegalCharacters,
  calcCharactersLength
} from '../../../../utils/utils'
import CONFIG from '../../../../constants/config'
import ContactUS from '../../../components/ContactUS'

const RadioGroup = Radio.Group
const Option = Select.Option

const namingExample: Record<string, string> = {
  user: '王某',
  time: '2020-09-03 20_41_18',
  address: '北京市三里屯',
  watermark: '打卡水印',
  photoNumber: '180-090-1234'
}

const presetRuleArray = ['user', 'time', 'address', 'watermark', 'photoNumber']

interface FolderPhotoTitle {
  titleId: string
  titleName: string
  [key: string]: any
}

interface TimeParams {
  startTime: number
  endTime: number
}

interface FolderModalProps {
  visible: boolean
  onClose: () => void
  onOk: (namingRule: string[], timeParams: TimeParams) => void
  folderPhotoTitlesList: FolderPhotoTitle[]
  isBatchDownload?: boolean
  selectedFolderIds?: string[]
}

function FolderModal({
  visible,
  onClose,
  onOk,
  folderPhotoTitlesList,
  isBatchDownload,
  selectedFolderIds
}: FolderModalProps) {
  const [namingRule, setNamingRule] = useState<string[]>([
    'user',
    'time',
    'watermark'
  ])
  const [timeType, setTimeType] = useState('custom')
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().startOf('month'))
  const [endDate, setEndDate] = useState<Dayjs>(dayjs().endOf('month'))
  const [namingInputVisible, setNamingInputVisible] = useState(false)

  // 从 Redux 获取状态
  const isVip = useAppSelector((state) => state.group.isVip)
  const groupId = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)

  // 保存上一次的 visible 值
  const prevVisibleRef = useRef<boolean>()

  useEffect(() => {
    if (prevVisibleRef.current !== visible && visible === true) {
      const storedNamingRule = JSON.parse(
        localStorage.getItem('namingRule') || 'null'
      )
      if (storedNamingRule) {
        // 过滤当前选中的图片标签中 不包含上次选择的图片标签
        // 图片命名规则回显
        const ruleFolderPhotoTitlesList = formatTitlesData(
          folderPhotoTitlesList
        )
        const namingList = storedNamingRule.reduce((prev, cur) => {
          if (cur.includes('title')) {
            const id = cur.split('title')[1]
            for (const item of ruleFolderPhotoTitlesList) {
              if (item.titleId === id) {
                prev.push('title' + id)
                break
              }
            }
          } else {
            prev.push(cur)
          }
          return prev
        }, [])
        setNamingRule(namingList)
      }
    }
    prevVisibleRef.current = visible
  }, [visible, folderPhotoTitlesList])

  useEffect(() => {
    if (visible && !isVip) {
      sensors.track('web_show_page_openapp_workgroupvip', {
        pagePath: 'photoAlbums',
        functionType: 'downloadPhoto'
      })
    }
  }, [visible, isVip])

  const handleSensorClickItem = (clickItem: string) => {
    sensors.track('web_click_page_workgroup_album_download', {
      clickItem,
      groupID: groupId,
      role: sensorsRole(myRole),
      isVip
    })
  }

  const onChangeData = (value: string[], list: any[]) => {
    if (value.length > 5) {
      value = value.slice(0, 5)
      list = list.slice(0, 5)
      message.warning('最多支持选择5个命名条件!')
    }

    const processedValue = value.reduce((prev: string[], cur: string) => {
      const initLength = cur.length
      cur = raplaceEmoji(cur)
      cur = filterIllegalCharacters(cur)
      if (initLength !== cur.length) {
        message.warning('特殊字符已过滤！')
      }

      if (cur.trim() !== '') {
        if (!cur.includes('title') && calcCharactersLength(cur) > 32) {
          cur = sliceCharactersLen(cur, 32)
          message.warning('最多支持16个汉字或32个字符！')
        }
        prev.push(cur)
      } else {
        list.splice(prev.length, 1)
      }
      return prev
    }, [])

    setNamingRule(processedValue)
  }

  const reportSaClickEvent = ({
    clickItem,
    folderID = '',
    folderName = ''
  }: {
    clickItem: string
    folderID?: string
    folderName?: string
  }) => {
    sensors.track('web_workgroup_folders_click', {
      clickItem,
      groupID: groupId,
      role: sensorsRole(myRole),
      folderID: folderID + '',
      folderName,
      track_sign: 'aworkgroup.b4378.action.web_workgroup_folders_click',
      requirement_id: 221
    })
  }

  const handleOk = () => {
    handleSensorClickItem('download')

    // 如果是批量下载，调用 reportSaClickEvent 埋点
    if (isBatchDownload) {
      reportSaClickEvent({
        clickItem: '批量下载',
        folderID: selectedFolderIds?.join(',') || '', // 传递选中的文件夹ID列表
        folderName: '批量下载确认'
      })
    }

    localStorage.setItem('namingRule', JSON.stringify(namingRule))

    // 文件命名规则：1、自定义：diy:+name, 2、 头目：title:+name, 3、其他
    const parsedNamingRule = namingRule.reduce(
      (prev: string[], cur: string) => {
        if (cur.includes('title')) {
          const value = cur.split('title')[1]
          prev.push('title:' + value)
        } else if (!presetRuleArray.includes(cur)) {
          prev.push('diy:' + cur)
        } else {
          prev.push(cur)
        }
        return prev
      },
      []
    )

    // 处理时间范围参数
    const timeParams: TimeParams = { startTime: 0, endTime: 0 }
    if (timeType === 'custom') {
      // 自定义时间范围：把起始日归到当天开始（00:00:00.000）、结束日归到当天结束
      // （23:59:59.999），确保覆盖结束日一整天。
      // antd6 的 DatePicker 选中某天后时间部分是当天 00:00:00，若直接取 valueOf，
      // 选同一天下载会让 startTime 与 endTime 都等于当天 00:00:00（区间为空），
      // 接口查不到任何照片；选多天也会漏掉结束日 00:00 之后的照片。
      timeParams.startTime = startDate ? startDate.startOf('day').valueOf() : 0
      timeParams.endTime = endDate ? endDate.endOf('day').valueOf() : 0
    } else {
      // 全部，传递0
      timeParams.startTime = 0
      timeParams.endTime = 0
    }

    onOk(parsedNamingRule, timeParams)
  }

  const handleClose = () => {
    handleSensorClickItem('cancel')
    onClose()
  }

  // 过滤相同name项合并id
  const formatTitlesData = (titlesData: FolderPhotoTitle[]) => {
    return titlesData.reduce(
      (prev: FolderPhotoTitle[], cur: FolderPhotoTitle) => {
        for (const item of prev) {
          if (cur.titleName === item.titleName) {
            item.titleId = item.titleId + ',' + cur.titleId
            return prev
          }
        }
        prev.push({ ...cur })
        return prev
      },
      []
    )
  }

  const rulePhotoList = formatTitlesData(folderPhotoTitlesList)
  const exampleName =
    namingRule
      .map((rule, index) => {
        if (namingExample[rule]) {
          return namingExample[rule]
        } else {
          if (rule.includes('title')) {
            for (const items of rulePhotoList) {
              const value = rule.split('title')[1]
              if (value === items.titleId) {
                return items.titleName
              }
            }
          }
        }
        return rule
      })
      .join(' ') + '.jpg'

  return (
    <Modal
      open={visible}
      className="folder-modal albums-modal folder-style"
      getContainer={false}
      width={511}
      centered
      title="文件夹下载"
      mask={{ closable: false }}
      {...Object.assign({}, isVip ? null : { footer: null })}
      closeIcon={
        <ContactUS fromPage="分类相册">
          <span
            className="contact-item"
            onClick={(e) => {
              handleSensorClickItem('contactUs')
            }}
          >
            <img style={{ width: '105px' }} src={contact_us_icon} />
          </span>
        </ContactUS>
      }
      onCancel={handleClose}
      okText="下载"
      okButtonProps={{
        disabled: namingRule.length === 0
      }}
      onOk={handleOk}
    >
      <div>
        <span>下载范围：</span>
        <RadioGroup
          value={timeType}
          onChange={(e) => setTimeType(e.target.value)}
        >
          <Radio value="custom">自定义日期范围</Radio>
          <Radio value="">全部</Radio>
        </RadioGroup>
      </div>
      {!!timeType && (
        <div>
          <DatePicker
            style={{ marginLeft: '70px', marginTop: '10px' }}
            value={startDate}
            onChange={(date) => date && setStartDate(date)}
            disabledDate={(currentDate) => {
              return (
                // Math.abs(currentDate.diff(endDate, 'months', true)) > 6 ||
                currentDate > endDate
              )
            }}
          />
          <span style={{ margin: '0 6px' }}>至</span>
          <DatePicker
            value={endDate}
            onChange={(date) => date && setEndDate(date)}
            disabledDate={(currentDate) => {
              return (
                // Math.abs(currentDate.diff(startDate, 'months', true)) > 6 ||
                currentDate < startDate
              )
            }}
          />
        </div>
      )}

      <p>
        文件内照片命名示例：
        {!namingInputVisible && (
          <Button type="link" onClick={() => setNamingInputVisible(true)}>
            修改
          </Button>
        )}
      </p>
      {namingInputVisible && (
        <Select
          mode="tags"
          style={{ width: '100%' }}
          placeholder="请选择或输入内容进行命名"
          value={namingRule}
          onChange={onChangeData}
        >
          <Option key="user">拍摄人姓名</Option>
          <Option key="time">拍摄日期</Option>
          <Option key="address">拍摄地点</Option>
          <Option key="watermark">水印名称</Option>
          <Option key="photoNumber">照片编号</Option>
          {rulePhotoList.map((item) => (
            <Option key={'title' + item.titleId}>{item.titleName}</Option>
          ))}
        </Select>
      )}
      {namingRule.length ? (
        <div style={{ marginTop: '10px' }}>
          <div className="example">
            <img src={naming_icon} alt="" />
            <p>{exampleName}</p>
          </div>
        </div>
      ) : (
        <p style={{ color: '#f5222d' }}>请至少选择一项</p>
      )}

      {!isVip && (
        <Advance
          onCancel={handleClose}
          onOk={() => {
            handleSensorClickItem('download')
          }}
        />
      )}
    </Modal>
  )
}

export default FolderModal
